import { extractKeyValuePairs } from "./util";

/**
 * Utility function to extract operator and value from search strings like ">500", "<1000", "=200"
 * @param searchValue - The search value that may contain an operator
 * @returns Object with operator and numeric value, or null if parsing fails
 */
export function parseComparisonOperator(searchValue: string): {
  operator: string | null;
  value: string;
} {
  const match = searchValue.match(/^([><=]+)(.+)$/);
  if (match) {
    const [, operator, value] = match;
    return { operator, value };
  }
  return { operator: null, value: searchValue };
}

export function extractKeyedSearchTerms(
  search: string
): [{ key: string; value: string[] }[], string] {
  const keyValuePairsWithPositions = extractKeyValuePairs(search);

  const keyValuePairs = keyValuePairsWithPositions.map(({ key, value }) => ({
    key,
    value,
  }));

  // Construct the unmatched portion by excluding matched segments
  let unmatchedSearch = "";
  let currentIndex = 0;

  keyValuePairsWithPositions.forEach(({ start, end }) => {
    if (currentIndex < start) {
      unmatchedSearch += search.slice(currentIndex, start) + " ";
    }
    currentIndex = end;
  });

  // Append any remaining unmatched portion after the last match
  if (currentIndex < search.length) {
    unmatchedSearch += search.slice(currentIndex);
  }

  return [keyValuePairs, unmatchedSearch.trim()];
}

/**
 * Remove every occurrence of a single keyed term (e.g. `profileNumber:AMZ`) from
 * a search string, leaving all other keyed terms and free text intact. Handles
 * quoted, comma- and pipe-separated values via the same grammar as
 * extractKeyValuePairs (we slice out the matched span rather than regex the raw
 * string, so `profileNumber:"AMZ NH"` is removed whole). Used by callers that
 * consume one key manually and hand the remainder to the generic parser — unlike
 * the second element of extractKeyedSearchTerms, which drops ALL keyed terms.
 */
export function removeKeyedSearchTerm(search: string, key: string): string {
  // Remove matched spans from the end backwards so earlier offsets stay valid.
  const spans = extractKeyValuePairs(search)
    .filter((pair) => pair.key === key)
    .sort((a, b) => b.start - a.start);

  let result = search;
  for (const { start, end } of spans) {
    result = result.slice(0, start) + result.slice(end);
  }

  return result.replace(/\s+/g, " ").trim();
}
